import { Button, Modal, Affix, Icon, Tooltip } from "antd";
import { faMessageBot, faAngleDown, faChevronUp, faArrowsMinimize, faArrowsMaximize } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useContext, useEffect, useRef, useState, useMemo, useCallback } from "react";
import { ApplicationContext } from "../context";
import DeveloperChatTool from "./developer-chat-tool";
import { BrandType } from "../types/enumerations/common";
import { Brand } from "../types/interfaces/onload";

interface IFloatingChatButtonProps {
    userFeatures: Array<any>;
}

const FloatingChatButton: React.FC<IFloatingChatButtonProps> = ({ userFeatures }) => {
    const { selectedBrand, onload } = useContext(ApplicationContext);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [unsavedChangesExist, setUnsavedChangesExist] = useState(false);
    const [isSticky, setIsSticky] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [sessionTimeoutModalBlocksChat, setSessionTimeoutModalBlocksChat] = useState(false);
    const previousBrandIdRef = useRef<number | undefined>(selectedBrand?.id);

    // Check if developer chat feature is enabled
    const isDeveloperChatEnabled = userFeatures.find(feat => feat.id === 6);

    // All hooks must be called before any early returns
    const iFrame = useMemo(() => ({
        id: "floating_developer_chat_frame",
        name: "floating_developer_chat_container"
    }), []);

    // Memoize brand object to prevent unnecessary re-renders of DeveloperChatTool
    const brand = useMemo((): Brand & { channel?: Brand } | null => {
        if (!onload) return null;
        const brandValue = selectedBrand.id ? selectedBrand : onload?.financialInstitution as Brand;
        return brandValue as Brand & { channel?: Brand };
    }, [selectedBrand?.id, selectedBrand?.name, selectedBrand?.type?.id, onload?.financialInstitution?.id]);

    // Memoize callbacks to prevent unnecessary re-renders
    const handleBrandChange = useCallback((brand: Brand) => {
        // Handle brand change if needed
    }, []);

    const handleDataChanged = useCallback((isChanged: boolean) => {
        setUnsavedChangesExist(isChanged);
    }, []);

    // Watch for cobrand id changes and auto-open chat if it was already open
    useEffect(() => {
        const currentBrandId = selectedBrand?.id;
        // If brand changed and chat was open, reload the iframe by keeping it open
        if (previousBrandIdRef.current !== undefined && currentBrandId !== previousBrandIdRef.current && isChatOpen) {
            // The DeveloperChatTool will automatically reload the iframe when brand.id changes
            // due to the useEffect dependency in developer-chat-tool.tsx
        }
        previousBrandIdRef.current = currentBrandId;
    }, [selectedBrand?.id, isChatOpen]);

    useEffect(() => {
        const handleDeveloperChatSessionEvent = (e: Event) => {
            const detail = (e as CustomEvent<{ sessionTimeoutModalVisible?: boolean }>).detail;

            if (detail?.sessionTimeoutModalVisible === true) {
                setSessionTimeoutModalBlocksChat(true);
                if (isChatOpen && !isMinimized) {
                    setIsMinimized(true);
                    setIsFullscreen(false);
                }
            } else if (detail?.sessionTimeoutModalVisible === false) {
                setSessionTimeoutModalBlocksChat(false);
            } else if (!detail) {
                // Plain minimizeDeveloperChat (no detail): minimize only
                if (isChatOpen && !isMinimized) {
                    setIsMinimized(true);
                    setIsFullscreen(false);
                }
            }
        };

        window.addEventListener("minimizeDeveloperChat", handleDeveloperChatSessionEvent);
        return () => window.removeEventListener("minimizeDeveloperChat", handleDeveloperChatSessionEvent);
    }, [isChatOpen, isMinimized]);

    // Don't render until onload is available (after all hooks)
    if (!isDeveloperChatEnabled || !onload || !brand) {
        return null;
    }

    const handleChatToggle = () => {
        if (sessionTimeoutModalBlocksChat) return;
        setIsChatOpen(!isChatOpen);
        setIsMinimized(false);
        setIsFullscreen(false);
    };

    const handleMinimize = () => {
        if (sessionTimeoutModalBlocksChat) return;
        setIsMinimized(true);
        setIsFullscreen(false);
    };

    const handleMaximize = () => {
        if (sessionTimeoutModalBlocksChat) return;
        setIsFullscreen(true);
        setIsMinimized(false);
    };

    const handleRestore = () => {
        if (sessionTimeoutModalBlocksChat) return;
        setIsFullscreen(false);
        setIsMinimized(false);
    };

    const handleChatClose = () => {
        if (unsavedChangesExist) {
            Modal.confirm({
                title: "You have unsaved changes.",
                content: "Are you sure you want to close the chat?",
                onOk: () => {
                    setUnsavedChangesExist(false);
                    setIsChatOpen(false);
                    setIsMinimized(false);
                    setIsFullscreen(false);
                    setIsHovered(false);
                },
            });
        } else {
            setIsChatOpen(false);
            setIsMinimized(false);
            setIsFullscreen(false);
            setIsHovered(false);
        }
    };

    const blockChatPointer = sessionTimeoutModalBlocksChat ? ("none" as const) : undefined;

    return (
        <div style={{ pointerEvents: blockChatPointer }}>
            {/* Floating Chat Button */}
            {!isChatOpen && (
                <Affix 
                    onChange={(affixed) => setIsSticky(!!affixed)}
                    style={{
                        position: "fixed",
                        bottom: "55px",
                        right: "14px",
                        zIndex: 1000,
                    }}
                >
                    <div className="align-right" style={{ float: "right" }}>
                        <div
                            className="floating-chat-button-wrapper"
                            style={{
                                position: "relative",
                                display: "inline-block",
                            }}
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                        >
                            <Button 
                                type="primary" 
                                className="box-shadow margin-top-sm" 
                                onClick={handleChatToggle}
                            >
                                <Icon className="font-size-lg" component={() => <FontAwesomeIcon icon={faMessageBot} />} />
                                {isHovered && <span>Chat with Yodlee</span>}
                            </Button>
                        </div>
                        <span className="margin-xs-horizontal" />
                    </div>
                </Affix>
            )}

            {/* Chat Full Window Modal */}
            {isChatOpen && (
                <>
                    {/* Backdrop Overlay */}
                    {!isMinimized && !isFullscreen && (
                        <div
                            style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: "rgba(0, 0, 0, 0.45)",
                                zIndex: 1000,
                            }}
                            onClick={handleMinimize}
                        />
                    )}
                    
                    {/* Chat Modal */}
                    <div
                        className={isFullscreen ? "floating-chat-modal-fullscreen" : ""}
                        style={{
                            position: "fixed",
                            ...(isMinimized ? {
                                bottom: "55px",
                                right: "14px",
                                top: "auto",
                                left: "auto",
                                transform: "none",
                                width: "400px",
                                height: "60px",
                                borderRadius: "8px"
                            } : isFullscreen ? {
                                top: "0",
                                left: "0",
                                right: "0",
                                bottom: "0",
                                transform: "none",
                                width: "100vw",
                                height: "100vh",
                                borderRadius: "0"
                            } : {
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                bottom: "auto",
                                right: "auto",
                                width: "90%",
                                maxWidth: "1200px",
                                height: "90%",
                                maxHeight: "800px",
                                borderRadius: "8px"
                            }),
                            backgroundColor: "#fff",
                            boxShadow: isMinimized ? "none" : "0 4px 24px rgba(0, 0, 0, 0.15)",
                            zIndex: 1001,
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            border: isMinimized ? "none" : "1px solid #e8e8e8",
                            transition: "all 0.3s ease-in-out"
                        }}
                    >
                        {/* Chat Header */}
                        <div className="floating-chat-header">
                            <div className="floating-chat-header-left">
                                <Button
                                    ghost
                                    size="small"
                                    onClick={handleChatClose}
                                    className="floating-chat-end-button"
                                >
                                    End Chat
                                </Button>
                            </div>
                            <div className="floating-chat-header-center">
                                <FontAwesomeIcon icon={faMessageBot} style={{ fontSize: "20px", color: "#1890ff" }} />
                                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#1890ff" }}>
                                    AskYodlee
                                </h3>
                            </div>
                            <div className="floating-chat-header-actions">
                                {isMinimized ? (
                                    <Button
                                        ghost
                                        size="small"
                                        onClick={handleRestore}
                                        className="floating-chat-action-button"
                                        title="Restore"
                                    >
                                        <FontAwesomeIcon 
                                            icon={faChevronUp} 
                                            style={{ fontSize: "16px" }} 
                                        />
                                    </Button>
                                ) : isFullscreen ? (
                                    <>
                                        <Button
                                            ghost
                                            size="small"
                                            onClick={handleRestore}
                                            className="floating-chat-action-button"
                                            title="Restore"
                                        >
                                            <FontAwesomeIcon 
                                                icon={faArrowsMinimize} 
                                                style={{ fontSize: "16px" }} 
                                            />
                                        </Button>
                                        <Button
                                            ghost
                                            size="small"
                                            onClick={handleMinimize}
                                            className="floating-chat-action-button"
                                            title="Minimize"
                                        >
                                            <FontAwesomeIcon 
                                                icon={faAngleDown} 
                                                style={{ fontSize: "16px" }} 
                                            />
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            ghost
                                            size="small"
                                            onClick={handleMaximize}
                                            className="floating-chat-action-button"
                                            title="Maximize"
                                        >
                                            <FontAwesomeIcon 
                                                icon={faArrowsMaximize} 
                                                style={{ fontSize: "16px" }} 
                                            />
                                        </Button>
                                        <Button
                                            ghost
                                            size="small"
                                            onClick={handleMinimize}
                                            className="floating-chat-action-button"
                                            title="Minimize"
                                        >
                                            <FontAwesomeIcon 
                                                icon={faAngleDown} 
                                                style={{ fontSize: "16px" }} 
                                            />
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Chat Content */}
                        <div className="floating-chat-content" style={{ 
                            opacity: isMinimized ? 0 : 1,
                            visibility: isMinimized ? "hidden" : "visible",
                            pointerEvents: isMinimized ? "none" : "auto",
                            transition: "opacity 0.3s ease-in-out",
                            display: isMinimized ? "none" : "flex",
                            flexDirection: "column"
                        }}>
                            {brand && (
                                <DeveloperChatTool
                                    iFrame={iFrame}
                                    brand={brand}
                                    onBrandChange={handleBrandChange}
                                    onDataChanged={handleDataChanged}
                                />
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default FloatingChatButton;
